import { useCanEdit } from '@/auth/account';
import { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { Group, InvestmentStatus } from '@/api/types';
import {
  useCreateInvestment,
  useDeleteInvestment,
  useInvestment,
  useUpdateInvestment,
} from '@/api/hooks';
import { centsToInput, formatMoney, parseMoneyToCents } from '@/lib/format';
import { STATUS_META, STATUS_ORDER } from '@/lib/status';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Field, Select, TextArea, TextInput } from './ui/Field';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { PositionEditor } from './PositionEditor';
import { LinkList, type LinkDraft } from './LinkList';
import { isHttpUrl, normalizeUrl } from '@/lib/links';

interface InvestmentDialogProps {
  open: boolean;
  /** null → create mode. */
  investmentId: string | null;
  groups: Group[];
  currency: string;
  /** Preselected group when creating from a group card. */
  defaultGroupId?: string | null;
  onClose: () => void;
}

interface FormState {
  title: string;
  groupId: string;
  status: InvestmentStatus;
  estimatedCost: string;
  plannedFrom: string;
  plannedTo: string;
  notes: string;
  links: LinkDraft[];
}

const EMPTY: FormState = {
  title: '',
  groupId: '',
  status: 'idea',
  estimatedCost: '',
  plannedFrom: '',
  plannedTo: '',
  notes: '',
  links: [],
};

export function InvestmentDialog({
  open,
  investmentId,
  groups,
  currency,
  defaultGroupId,
  onClose,
}: InvestmentDialogProps) {
  const isEdit = investmentId !== null;
  const { data: investment } = useInvestment(open && isEdit ? investmentId : null);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canEdit = useCanEdit();

  const createInvestment = useCreateInvestment();
  const updateInvestment = useUpdateInvestment();
  const deleteInvestment = useDeleteInvestment();

  // Load the record into the form once it arrives; reset for create mode.
  useEffect(() => {
    if (!open) return;
    if (isEdit && investment) {
      setForm({
        title: investment.title,
        groupId: investment.groupId ?? '',
        status: investment.status,
        estimatedCost: centsToInput(investment.estimatedCost),
        plannedFrom: investment.plannedFrom ?? '',
        plannedTo: investment.plannedTo ?? '',
        notes: investment.notes ?? '',
        links: investment.links.map((link) => ({ label: link.label ?? '', url: link.url })),
      });
    } else if (!isEdit) {
      setForm({ ...EMPTY, groupId: defaultGroupId ?? '' });
    }
    setError(null);
  }, [open, isEdit, investment, defaultGroupId]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const save = () => {
    const title = form.title.trim();
    if (title === '') {
      setError('Bitte einen Titel angeben.');
      return;
    }
    if (form.plannedTo && !form.plannedFrom) {
      setError('Bitte zuerst den Startmonat angeben.');
      return;
    }
    if (form.plannedFrom && form.plannedTo && form.plannedTo < form.plannedFrom) {
      setError('Der Endmonat darf nicht vor dem Startmonat liegen.');
      return;
    }
    // Empty rows are dropped; "www.example.de" becomes "https://www.example.de".
    const links = form.links
      .map((link) => ({ label: link.label.trim() || null, url: normalizeUrl(link.url) }))
      .filter((link) => link.url !== '');
    if (links.some((link) => !isHttpUrl(link.url))) {
      setError('Bitte nur gültige Links mit http:// oder https:// angeben.');
      return;
    }

    const payload = {
      title,
      groupId: form.groupId === '' ? null : form.groupId,
      status: form.status,
      estimatedCost: parseMoneyToCents(form.estimatedCost),
      plannedFrom: form.plannedFrom === '' ? null : form.plannedFrom,
      // A window of a single month is stored as one month, not as a range onto itself.
      plannedTo:
        form.plannedTo === '' || form.plannedTo === form.plannedFrom ? null : form.plannedTo,
      notes: form.notes.trim() === '' ? null : form.notes.trim(),
      links,
    };

    const onError = (mutationError: Error) => setError(mutationError.message);
    if (isEdit) {
      updateInvestment.mutate({ id: investmentId, ...payload }, { onSuccess: onClose, onError });
    } else {
      createInvestment.mutate(payload, { onSuccess: onClose, onError });
    }
  };

  const pending = createInvestment.isPending || updateInvestment.isPending;
  const positions = investment?.positions ?? [];
  const positionsTotal = positions.reduce((sum, position) => sum + position.total, 0);

  return (
    <>
      <Modal
        open={open}
        size={isEdit ? 'wide' : 'md'}
        title={!canEdit ? 'Investition ansehen' : isEdit ? 'Investition bearbeiten' : 'Neue Investition'}
        description={
          isEdit
            ? 'Positionen überschreiben die Kostenschätzung, sobald mindestens eine erfasst ist.'
            : 'Kosten lassen sich schätzen oder später in Positionen aufschlüsseln.'
        }
        onClose={onClose}
        footer={
          !canEdit ? (
            <Button onClick={onClose}>Schließen</Button>
          ) : (
          <>
            {isEdit ? (
              <Button
                variant="ghost"
                onClick={() => setConfirmDelete(true)}
                className="mr-auto hover:text-[var(--critical)]"
              >
                <Trash2 size={15} />
                Löschen
              </Button>
            ) : null}
            <Button onClick={onClose}>Abbrechen</Button>
            <Button variant="primary" onClick={save} disabled={pending}>
              {pending ? 'Speichern …' : 'Speichern'}
            </Button>
          </>
          )
        }
      >
        {/* Viewers see the same form, but every field is disabled. */}
        <fieldset disabled={!canEdit} className="m-0 min-w-0 border-0 p-0">
        <div className="flex flex-col gap-4">
          <Field label="Titel" error={error}>
            {(id) => (
              <TextInput
                id={id}
                value={form.title}
                autoFocus={!isEdit}
                placeholder="z. B. Überdachung"
                onChange={(event) => set('title', event.target.value)}
              />
            )}
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Gruppe">
              {(id) => (
                <Select
                  id={id}
                  value={form.groupId}
                  onChange={(event) => set('groupId', event.target.value)}
                >
                  <option value="">Ohne Gruppe</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Status">
              {(id) => (
                <Select
                  id={id}
                  value={form.status}
                  onChange={(event) => set('status', event.target.value as InvestmentStatus)}
                >
                  {STATUS_ORDER.map((status) => (
                    <option key={status} value={status}>
                      {STATUS_META[status].label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <Field
            label="Kostenschätzung"
            hint={
              positions.length > 0
                ? `Wird derzeit von ${positions.length} Position(en) über ${formatMoney(
                    positionsTotal,
                    currency,
                  )} überschrieben.`
                : 'Gilt, solange keine Positionen erfasst sind.'
            }
          >
            {(id) => (
              <TextInput
                id={id}
                inputMode="decimal"
                className="num"
                placeholder="0,00"
                value={form.estimatedCost}
                onChange={(event) => set('estimatedCost', event.target.value)}
              />
            )}
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Geplant ab (Monat)"
              hint="Geplant wird in ganzen Monaten – ein Tag wäre hier Scheingenauigkeit."
            >
              {(id) => (
                <TextInput
                  id={id}
                  type="month"
                  value={form.plannedFrom}
                  onChange={(event) => set('plannedFrom', event.target.value)}
                />
              )}
            </Field>
            <Field label="Geplant bis (Monat)" hint="Leer lassen, wenn es ein einzelner Monat ist.">
              {(id) => (
                <TextInput
                  id={id}
                  type="month"
                  value={form.plannedTo}
                  min={form.plannedFrom || undefined}
                  onChange={(event) => set('plannedTo', event.target.value)}
                />
              )}
            </Field>
          </div>

          <Field label="Notizen">
            {(id) => (
              <TextArea
                id={id}
                value={form.notes}
                placeholder="Details, Ansprechpartner, Angebotsnummern …"
                onChange={(event) => set('notes', event.target.value)}
              />
            )}
          </Field>

          <section aria-label="Links">
            <h3 className="mb-2 text-sm font-medium text-[var(--ink-2)]">Links</h3>
            <LinkList links={form.links} onChange={(links) => set('links', links)} />
          </section>

          {isEdit && investment ? (
            <section className="border-t border-[var(--border)] pt-4">
              <h3 className="mb-3 text-sm font-semibold tracking-tight">Positionen</h3>
              <PositionEditor
                investmentId={investment.id}
                positions={positions}
                currency={currency}
              />
            </section>
          ) : null}
        </div>
        </fieldset>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Investition löschen?"
        message="Die Investition und alle zugehörigen Positionen werden dauerhaft entfernt."
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          if (!investmentId) return;
          deleteInvestment.mutate(investmentId, {
            onSuccess: () => {
              setConfirmDelete(false);
              onClose();
            },
          });
        }}
      />
    </>
  );
}
